import styled from 'styled-components'
import capitalize from 'lodash/capitalize'
import { ProductTabPanel } from '@/features/shop/components/ProductDetailCommon/ProductTabPanel'
import { NutritionalWarnings } from '@/features/shop/components/ProductDetailCommon/NutritionalWarnings'
import { screenSizes } from '@shipt/design-system-themes'
import { Banner } from '@/features/shared/elements/Banners'
import { InfoCircleIcon } from '@shipt/design-system-icons'
import { type Product } from '@/features/shop/services/Product/types'
import {
  useQueryAlcoholComplianceTerms,
  useQueryCMSPDPAdPlacement,
} from '@/features/cms/services/CMS/queries'
import { LayoutGroup } from '@/features/cms/components/CMS/components/LayoutGroup'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { ClientOnly } from '@/features/shared/components/ClientOnly'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { useMemo } from 'react'

type Props = {
  product: Product
}
export const ProductWarningsAndDetails = ({ product }: Props) => {
  const {
    description,
    nutritionalWarnings,
    nutritionalDetails,
    chokingHazard,
    allergenInfo,
    highlights,
    specifications,
    hasAlcoholTerms,
    id,
  } = product

  const { data } = useQueryAlcoholComplianceTerms(hasAlcoholTerms)
  const { data: pdpAds } = useQueryCMSPDPAdPlacement(id)

  const { screenMd: isTablet } = useMediaQuery()

  const renderPanel =
    Boolean(description) ||
    highlights.length > 0 ||
    specifications.length > 0 ||
    (Array.isArray(nutritionalDetails.products) && nutritionalDetails.products.length > 0) ||
    data?.data.show_on_pdp

  // Filter ad children to include only ad_showcase and ad_banner types based on current and future AdTech placement plans
  const adChildren = useMemo(
    () =>
      pdpAds?.data?.children?.filter(
        (child) => child.content_type_id === 'ad_showcase' || child.content_type_id === 'ad_banner',
      ),
    [pdpAds?.data?.children],
  )
  const renderAdPlacement = Boolean(adChildren?.length)

  const warningContent = (
    <>
      {!chokingHazard && !allergenInfo && nutritionalWarnings.length > 0 && (
        <WarningWrapper>
          <NutritionalWarnings warnings={nutritionalWarnings} />
        </WarningWrapper>
      )}
      {!!allergenInfo && (
        <WarningWrapper>
          <ProductWarning message={allergenInfo} />
        </WarningWrapper>
      )}
      {!!chokingHazard && (
        <WarningWrapper>
          <ProductWarning message={chokingHazard} />
        </WarningWrapper>
      )}
    </>
  )

  const panelContent = (
    <ProductTabPanel product={product} alcoholTerms={data} suppressNutritionalWarnings />
  )

  if (renderAdPlacement) {
    const showcaseElement = (
      // Showcase is an ad (not SEO-relevant), so we render it client-only in the
      // correct DOM position per breakpoint. This gives correct tab order on both
      // mobile and tablet without mounting anything twice. Grid areas handle
      // visual placement; DOM order handles focus/tab sequence.
      <ClientOnly>
        {!!pdpAds && (
          <ShowcaseArea>
            <LayoutGroup {...pdpAds} data={{ ...pdpAds.data, children: adChildren }} />
          </ShowcaseArea>
        )}
      </ClientOnly>
    )

    return (
      <ShowcaseGrid $hasPanel={Boolean(renderPanel)}>
        {!isTablet && showcaseElement}
        {!!renderPanel && <PanelArea>{panelContent}</PanelArea>}
        <WarningsArea>{warningContent}</WarningsArea>
        {!!isTablet && showcaseElement}
      </ShowcaseGrid>
    )
  }

  return renderPanel ? (
    <>
      <PanelWrapper>{panelContent}</PanelWrapper>
      {warningContent}
    </>
  ) : (
    warningContent
  )
}

const ProductWarning = ({ message }: { message: string }) => (
  <Banner
    type="attention"
    icon={InfoCircleIcon}
    message={capitalize(message)}
    dangerouslySetInnerHTML
    tabIndex={-1}
  />
)

const PanelWrapper = styled.section`
  @media ${screenSizes.tablet} {
    width: 60%;
  }
`
const WarningWrapper = styled.div`
  margin-bottom: 16px;

  @media ${screenSizes.tablet} {
    width: 60%;
  }
`

/**
 * Grid container for the showcase layout. Visual placement is handled by
 * grid-template-areas; DOM/tab order is controlled by conditionally placing
 * the showcase element before or after the panel based on viewport.
 */
const ShowcaseGrid = styled.div<{ $hasPanel: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: ${({ $hasPanel }) =>
    $hasPanel
      ? `'warnings'
           'showcase'
           'panel'`
      : `'warnings'
           'showcase'`};

  @media ${screenSizes.tablet} {
    grid-template-areas: ${({ $hasPanel }) =>
      $hasPanel
        ? `'panel'
             'warnings'
             'showcase'`
        : `'warnings'
             'showcase'`};
  }
`

const PanelArea = styled(PanelWrapper)`
  grid-area: panel;
`

const WarningsArea = styled.div`
  grid-area: warnings;
`

const ShowcaseArea = styled.div`
  grid-area: showcase;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);

  @media ${screenSizes.tablet} {
    margin-left: 0;
    margin-right: 0;

    ${GridLayoutContainer} {
      --padding-inline: 0;
      --content-max-width: 100%;
    }
  }
`
